import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { CycleLog, CycleStats } from "../types/cycle";
import { addLogIDToDayPost, removeLogIDFromDayPost } from "./dayPostService";

/**
 * Convert date from YYYY-MM-DD format to YYYYMMDD format for dayPostID
 */
const formatDateToDayPostID = (dateString: string): string => {
  return dateString.replace(/-/g, "");
};

/**
 * Get all cycle logs from Firestore
 */
const getLogsFromFirestore = async (userId: string): Promise<CycleLog[]> => {
  try {
    const logsCollection = collection(db, "femaleCycleLogs", userId, "logs");
    const logsSnapshot = await getDocs(logsCollection);

    const logs: CycleLog[] = [];
    logsSnapshot.forEach((doc) => {
      logs.push({
        id: doc.id,
        ...doc.data(),
      } as CycleLog);
    });

    return logs;
  } catch (error) {
    console.error("Error reading from Firestore:", error);
    return [];
  }
};

/**
 * Recalculate cycle lengths for all cycles
 * Cycle length is calculated as the number of days between consecutive start dates
 * Implements SPEC 1 - Stored Cycle Data
 */
// const recalculateCycleLengths = (logs: CycleLog[]): CycleLog[] => {
//   // Sort by cycle_start_date ascending (oldest first)
//   const sortedLogs = [...logs].sort(
//     (a, b) =>
//       new Date(a.cycle_start_date).getTime() -
//       new Date(b.cycle_start_date).getTime()
//   );

//   // Calculate cycle_length for each cycle (except the most recent one)
//   const updatedLogs = sortedLogs.map((log, index) => {
//     if (index < sortedLogs.length - 1) {
//       const nextCycle = sortedLogs[index + 1];
//       const currentDate = new Date(log.cycle_start_date);
//       const nextDate = new Date(nextCycle.cycle_start_date);
//       const cycle_length = Math.round(
//         (nextDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24)
//       );
//       return { ...log, cycle_length };
//     } else {
//       // Most recent cycle has no length yet
//       return { ...log, cycle_length: undefined };
//     }
//   });

//   return updatedLogs;
// };
const recalculateCycleLengths = (logs: CycleLog[]): CycleLog[] => {
  const getDateString = (log: CycleLog) =>
    log.cycle_start_date || log.cycleStartDate || "";

  const sortedLogs = [...logs]
    .map((log) => {
      const dateStr = getDateString(log);
      const dateObj = new Date(dateStr);
      return {
        ...log,
        _sortDate: isNaN(dateObj.getTime()) ? 0 : dateObj.getTime(),
      };
    })
    .sort((a, b) => a._sortDate - b._sortDate)
    // .map(({ _sortDate, ...log }) => log;
    .map(({ _sortDate, ...log }) => log as CycleLog);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const updatedLogs = sortedLogs.map((log, index) => {
    const currentDateString = getDateString(log);
    const currentDate = new Date(currentDateString);

    if (isNaN(currentDate.getTime())) return log;
    currentDate.setHours(0, 0, 0, 0);

    if (index < sortedLogs.length - 1) {
      const nextCycle = sortedLogs[index + 1];
      const nextDateString = getDateString(nextCycle);
      const nextDate = new Date(nextDateString);

      if (isNaN(nextDate.getTime())) return log;
      nextDate.setHours(0, 0, 0, 0);

      const cycle_length = Math.round(
        (nextDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24),
      );
      return { ...log, cycle_length };
    } else {
      // FOR LATEST CYCLE: Use Garmin's length if available, otherwise calculate days since start
      const daysSoFar = Math.round(
        (today.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24),
      );

      // Priority: 1. Manual history length, 2. Garmin's length, 3. Days since start (only if > 0)
      const fallbackDays = daysSoFar > 0 ? daysSoFar : 0;
      const finalLength =
        log.cycle_length || log.cycleLengthDays || fallbackDays;

      return { ...log, cycle_length: finalLength };
    }
  });

  return updatedLogs;
};
/**
 * Update all cycle logs in Firestore after recalculation
 */
const updateLogsInFirestore = async (
  userId: string,
  logs: CycleLog[],
): Promise<void> => {
  try {
    const updatePromises = logs.map((log) => {
      if (!log.id) return Promise.resolve();
      const logDocRef = doc(db, "femaleCycleLogs", userId, "logs", log.id);
      const { id, ...logData } = log;

      // Remove undefined values - Firestore doesn't accept them
      const sanitizedData: any = {};
      Object.keys(logData).forEach((key) => {
        const value = (logData as any)[key];
        if (value !== undefined) {
          sanitizedData[key] = value;
        } else if (key === "notes") {
          // Ensure notes is always a string, even if empty
          sanitizedData[key] = "";
        }
      });

      return updateDoc(logDocRef, sanitizedData);
    });

    await Promise.all(updatePromises);
  } catch (error) {
    console.error("Error updating logs in Firestore:", error);
    throw error;
  }
};

/**
 * Add a new cycle log
 */
export const addCycleLog = async (
  userId: string,
  cycleLog: Omit<CycleLog, "timestamp" | "id" | "cycle_length">,
): Promise<string> => {
  try {
    // Create new log with timestamp and dayPostID
    const dayPostID = formatDateToDayPostID(cycleLog.cycle_start_date);
    const newLog = {
      cycle_start_date: cycleLog.cycle_start_date,
      dayPostID: dayPostID,
      notes: cycleLog.notes || "",
      timestamp: Date.now(),
    };

    // Add to Firestore
    const logsCollection = collection(db, "femaleCycleLogs", userId, "logs");
    const docRef = await addDoc(logsCollection, newLog);

    // Link the cycle log to the day post
    await addLogIDToDayPost(userId, dayPostID, docRef.id, "femaleCycleLogIDs");

    // Fetch all logs and recalculate cycle lengths
    const allLogs = await getLogsFromFirestore(userId);
    const recalculatedLogs = recalculateCycleLengths(allLogs);
    await updateLogsInFirestore(userId, recalculatedLogs);

    return docRef.id;
  } catch (error) {
    console.error("Error adding cycle log:", error);
    throw error;
  }
};

/**
 * Update an existing cycle log
 */
export const updateCycleLog = async (
  userId: string,
  logId: string,
  updates: Partial<Omit<CycleLog, "id" | "timestamp" | "cycle_length">>,
): Promise<void> => {
  try {
    // Get the current log to check if dayPostID is changing
    const logDocRef = doc(db, "femaleCycleLogs", userId, "logs", logId);
    const currentLogDoc = await getDoc(logDocRef);
    const currentLog = currentLogDoc.exists()
      ? (currentLogDoc.data() as CycleLog)
      : null;

    // Sanitize updates - ensure notes is empty string if undefined
    const sanitizedUpdates: any = { ...updates };
    if ("notes" in sanitizedUpdates && !sanitizedUpdates.notes) {
      sanitizedUpdates.notes = "";
    }

    // If cycle_start_date is being updated, recalculate dayPostID and handle day post links
    if (sanitizedUpdates.cycle_start_date) {
      const newDayPostID = formatDateToDayPostID(
        sanitizedUpdates.cycle_start_date,
      );
      sanitizedUpdates.dayPostID = newDayPostID;

      // If the dayPostID is changing, update the day post references
      if (
        currentLog &&
        currentLog.dayPostID &&
        currentLog.dayPostID !== newDayPostID
      ) {
        // Remove from old day post
        await removeLogIDFromDayPost(
          userId,
          currentLog.dayPostID,
          logId,
          "femaleCycleLogIDs",
        );
        // Add to new day post
        await addLogIDToDayPost(
          userId,
          newDayPostID,
          logId,
          "femaleCycleLogIDs",
        );
      }
    }

    // Update the specific log
    await updateDoc(logDocRef, sanitizedUpdates);

    // Fetch all logs and recalculate cycle lengths
    const allLogs = await getLogsFromFirestore(userId);
    const recalculatedLogs = recalculateCycleLengths(allLogs);
    await updateLogsInFirestore(userId, recalculatedLogs);
  } catch (error) {
    console.error("Error updating cycle log:", error);
    throw error;
  }
};

/**
 * Delete a cycle log
 */
export const deleteCycleLog = async (
  userId: string,
  logId: string,
): Promise<void> => {
  try {
    // First, get the cycle log to retrieve its dayPostID
    const logDocRef = doc(db, "femaleCycleLogs", userId, "logs", logId);
    const logDoc = await getDoc(logDocRef);

    if (logDoc.exists()) {
      const cycleLog = logDoc.data() as CycleLog;

      // Remove from day post if dayPostID exists
      if (cycleLog.dayPostID) {
        await removeLogIDFromDayPost(
          userId,
          cycleLog.dayPostID,
          logId,
          "femaleCycleLogIDs",
        );
      }
    }

    // Delete the specific log
    await deleteDoc(logDocRef);

    // Fetch all remaining logs and recalculate cycle lengths
    const allLogs = await getLogsFromFirestore(userId);
    const recalculatedLogs = recalculateCycleLengths(allLogs);
    await updateLogsInFirestore(userId, recalculatedLogs);
  } catch (error) {
    console.error("Error deleting cycle log:", error);
    throw error;
  }
};

/**
 * Get all cycle logs for a user
 */
export const getCycleLogs = async (userId: string): Promise<CycleLog[]> => {
  try {
    const logs = await getLogsFromFirestore(userId);
    // Sort by cycle_start_date descending (most recent first)
    // return logs.sort(
    //   (a, b) =>
    //     new Date(b.cycle_start_date).getTime() -
    //     new Date(a.cycle_start_date).getTime()
    // );
    return logs;
  } catch (error) {
    console.error("Error getting cycle logs:", error);
    throw error;
  }
};

/**
 * Get a single cycle log
 */
export const getCycleLog = async (
  userId: string,
  logId: string,
): Promise<CycleLog | null> => {
  try {
    const logDocRef = doc(db, "femaleCycleLogs", userId, "logs", logId);
    const logDoc = await getDoc(logDocRef);

    if (logDoc.exists()) {
      return {
        id: logDoc.id,
        ...logDoc.data(),
      } as CycleLog;
    }

    return null;
  } catch (error) {
    console.error("Error getting cycle log:", error);
    throw error;
  }
};

/**
 * Calculate cycle statistics
 * Implements SPEC 1 - Stored Cycle Data and Average Cycle Length Calculation
 */
/**
 * Calculate cycle statistics
 * Implements SPEC 1 - Stored Cycle Data and Average Cycle Length Calculation
 */
export const calculateCycleStats = (logs: CycleLog[]): CycleStats | null => {
  if (logs.length === 0) {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // 1. FILTER: Remove any logs that are in the future
  // This prevents the 2026 bug where future dates confuse the tracker.
  const validLogs = logs.filter((log) => {
    const dateStr = log.cycle_start_date || log.cycleStartDate || "";
    const logDate = new Date(dateStr);
    logDate.setHours(0, 0, 0, 0);
    return logDate <= today; // Only keep dates that are today or in the past
  });

  // Safety check: if all logs were in the future, return null or empty stats
  if (validLogs.length === 0) {
    return null;
  }

  // 2. SORT: Sort valid logs by start date descending (most recent first)
  const sortedLogs = [...validLogs].sort((a, b) => {
    const dateA = a.cycle_start_date || a.cycleStartDate || "";
    const dateB = b.cycle_start_date || b.cycleStartDate || "";
    return dateB.localeCompare(dateA);
  });

  // The most recent log is now guaranteed to be in the past
  const mostRecentDateStr = sortedLogs[0]?.cycle_start_date || sortedLogs[0]?.cycleStartDate || "";
  const mostRecentId = sortedLogs[0]?.id;

  // 3. CALCULATE AVERAGE: Filter for completed cycles ONLY
  // We exclude the most recent log because its length is just "days so far" (e.g., 113), not a full cycle.
  // We also apply the sanity checks (15-60 days).
  const completedCycles = sortedLogs.filter((log) => {
    // Exclude the active cycle
    if (log.id === mostRecentId) return false;

    // Sanity checks
    return (
      log.cycle_length &&
      log.cycle_length >= 15 &&
      log.cycle_length <= 60
    );
  });

  const cycle_lengths = completedCycles.map((log) => log.cycle_length!);

  // Calculate average_cycle_length
  // Default to 28 if we don't have enough history
  const average_cycle_length =
    cycle_lengths.length > 0
      ? Math.round(
          cycle_lengths.reduce((sum, length) => sum + length, 0) /
            cycle_lengths.length,
        )
      : 28;

  const shortest_cycle =
    cycle_lengths.length > 0 ? Math.min(...cycle_lengths) : 0;
  const longest_cycle =
    cycle_lengths.length > 0 ? Math.max(...cycle_lengths) : 0;

  // Extract dates for return
  const cycle_start_dates = sortedLogs.map((log) => log.cycle_start_date);

  // Predict next cycle date based on average
  let predicted_next_cycle_date: string | undefined;
  if (mostRecentDateStr && average_cycle_length > 0) {
    const lastStartDate = new Date(mostRecentDateStr);

    if (!isNaN(lastStartDate.getTime())) {
      const predictedDate = new Date(lastStartDate);
      predictedDate.setDate(predictedDate.getDate() + average_cycle_length);
      predicted_next_cycle_date = predictedDate.toISOString().split("T")[0];
    }
  }

  return {
    cycle_start_dates,
    cycle_lengths,
    average_cycle_length,
    shortest_cycle,
    longest_cycle,
    total_cycles: sortedLogs.length,
    most_recent_cycle_start_date: mostRecentDateStr,
    predicted_next_cycle_date,
  };
};
